/**
 * TallyPendingBell — floating badge icon for office-staff users
 * (KUMKUM, DHARMENDRA YADAV) and admins. Shows a count of
 * outstanding Tally Pending rows; tap → opens
 * `/tally-pending`.
 *
 * Behavior:
 *   - Polls `/api/sku/tally/pending/count` every 30 s.
 *   - Only renders for users with role `office_staff` or admin
 *     (admins already have access via the AdminPanel tab — the
 *      bell is an additional shortcut).
 *   - Placed bottom-right above StockTrailBell so they don't
 *     overlap.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_MOBILES = new Set(['9999313175', '9811117751', '9643558419']);
const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('session_token') || ''}`,
});

const TallyPendingBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const role = String(user?.role || '').toLowerCase();
  const mobile = String(user?.mobile || '');
  const enabled =
    role === 'office_staff' ||
    user?.is_admin === true ||
    ADMIN_MOBILES.has(mobile);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API}/sku/tally/pending/count`, {
        headers: auth(),
      });
      setCount(Number(data?.count || 0));
    } catch {
      /* silent — auth failures shouldn't surface a toast */
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [enabled, load]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-52 right-4 z-[60]">
      <button
        onClick={() => navigate('/tally-pending')}
        data-testid="tally-pending-bell"
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          count > 0
            ? 'bg-amber-500 hover:bg-amber-600'
            : 'bg-slate-700 hover:bg-slate-800'
        }`}
        title={`Tally Pending: ${count} item${count === 1 ? '' : 's'}`}
        aria-label="Tally Pending"
      >
        <ListChecks
          className={`w-5 h-5 text-white ${count > 0 ? 'animate-pulse' : ''}`}
        />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-white text-amber-700 text-[11px] font-bold flex items-center justify-center px-1"
            data-testid="tally-pending-bell-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
    </div>
  );
};

export default TallyPendingBell;
